import React from 'react';
import clsx from 'clsx';

import './FilterSearchPanel.css';
import { noop } from 'util/function';
import ActivityIndicator from 'components/activity/ActivityIndicator';
import FilterTree, { FilterTreeProps, TransformedFilterTreeNode, TreeDataProvider } from 'components/tree/FilterTree';

//
// --- Types ---

export interface FilterSearchPanelProps {
  className?: string;
  'data-testid'?: string;
  filterTreeData: ReadonlyArray<TransformedFilterTreeNode>;
  isFilterTreeDataLoading?: boolean;
  onFilterCheckedChange?: (filterId: string, isChecked: boolean) => void;
  onFilterCleared?: FilterTreeProps['onFilterClearClick'];
  onFilterOpenStateChange?: (openFilterId: string, isOpen: boolean) => void;
  openFilterIds?: ReadonlyArray<string>;
  render: boolean;
}

//
// --- FilterSearchPanel Component ---

const FilterSearchPanel: React.FC<FilterSearchPanelProps> = props => {
  const {
    className = '',
    'data-testid': dataTestId = 'filter-search-panel',
    filterTreeData = [],
    isFilterTreeDataLoading = false,
    onFilterCheckedChange = noop,
    onFilterCleared = noop,
    onFilterOpenStateChange = noop,
    openFilterIds = [],
    render
  } = props;

  const treeDataProviderRef = React.useRef(new TreeDataProvider({ data: filterTreeData }));

  React.useEffect(() => {
    if (filterTreeData !== null) {
      treeDataProviderRef.current.updateConfig({ data: filterTreeData });
      treeDataProviderRef.current.requestData();
    }
  }, [filterTreeData]);

  if (isFilterTreeDataLoading) {
    return <ActivityIndicator size={25} hideMessage />;
  }

  if (!Array.isArray(filterTreeData) || !filterTreeData.length) {
    return null;
  }

  const handleFilterTreeCheckboxChange: FilterTreeProps['onFilterCheckboxChange'] = (_, treeNode, isChecked) => {
    onFilterCheckedChange(treeNode.id, isChecked);
  };

  const handleFilterTreeOpenChange: FilterTreeProps['onNodeDisplayChange'] = treeNode => {
    onFilterOpenStateChange(treeNode.id, treeNode.open);
  };

  return (
    <>
      {render && (
        <div className={clsx('filter-search-panel', className)} data-testid={dataTestId}>
          <FilterTree
            onFilterCheckboxChange={handleFilterTreeCheckboxChange}
            onFilterClearClick={onFilterCleared}
            onNodeDisplayChange={handleFilterTreeOpenChange}
            openTreeNodeIds={openFilterIds}
            treeDataProvider={treeDataProviderRef.current}
          />
        </div>
      )}
    </>
  );
};

export default FilterSearchPanel;
